import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { redirect, useLoaderData } from 'react-router'
import { getSubdomainClient, prepareListing } from '~/lib.server'
import { activityHelper, findListing } from '~/mongo.server'
import Detail from './detail'

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { slug } = params
  const $or: {
    id?: string
    slug?: string
    tid?: string
  }[] = [{ slug }, { id: slug }]
  if (slug && slug.length >= 8) {
    // At least 9 characters long, so tid and dash
    $or.push({ tid: slug.split('-')[0] })
  }
  const [client, listing] = await Promise.all([
    getSubdomainClient(request.url),
    findListing({
      find: { $or },
      project: {
        source: 0,
        approved: 0,
        level: 0,
      },
    }),
  ])

  if (!listing || !client) {
    throw new Response('Not found', { status: 404 })
  }

  if (listing.slug !== slug) {
    return redirect(`/listings/${listing.slug}`, {
      status: 301,
    })
  }

  const prepared = prepareListing(
    listing,
    client!.featured ? (client.featured || []).map((f) => f.id) : [],
    client.listing_labels ?? {},
    client.labels ?? []
  )

  await activityHelper(request)
    .source('app')
    .type('view')
    .related('listing', listing)
    .write()

  return {
    listing: {
      ...prepared,
      labels: prepared.labels.filter((label) => label !== undefined),
    },
    client: client,
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = data?.listing.name || data?.listing.property.address
  if (title) {
    return [
      { title: `${title} - Resimplifi View` },
      { property: 'og:title', content: `${title} - Resimplifi View` },
      { property: 'twitter:title', content: `${title} - Resimplifi View` },
      {
        property: 'og:image',
        content:
          data?.listing.images.length > 0
            ? data?.listing.images[0].url
            : 'https://resimplifi.com/open-graph.png',
      },
      {
        property: 'twitter:image',
        content:
          data?.listing.images.length > 0
            ? data?.listing.images[0].url
            : 'https://resimplifi.com/open-graph.png',
      },
    ]
  }
  return []
}

export default function ListingDetail() {
  const { listing, client } = useLoaderData<typeof loader>()

  return <Detail listing={listing} client={client} />
}
